import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Container, PageHeader, Spinner } from "@/components/common";
import { LevelBadge } from "@/components/LevelBadge";
import { formatTime, formatPrice } from "@/lib/format";
import { MapPin, Clock, CalendarDays } from "lucide-react";
import { VenueMap } from "@/components/VenueMap";

export default function Classes() {
  const { data: classes, isLoading } = useQuery({ queryKey: ["classes"], queryFn: async () => (await api.get("/classes")).data });
  const byDay = {};
  (classes || []).forEach((c) => { (byDay[c.day] = byDay[c.day] || []).push(c); });

  return (
    <div>
      <PageHeader eyebrow="Weekly classes" title="Come and dance with us"
        subtitle="We run friendly classes across Telford &amp; Shropshire every week. Just turn up — no need to book, no partner required." />
      <Container className="py-10">
        {isLoading ? <Spinner /> : (
          <div className="space-y-10">
            {Object.entries(byDay).map(([day, list]) => {
              const dayVenue = (list.find((c) => c.venue) || {}).venue;
              return (
                <div key={day}>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <h2 className="flex items-center gap-2 font-display text-xl font-extrabold text-[#2D1E18]"><CalendarDays size={20} className="text-[#B04B26]" /> {day}</h2>
                    {dayVenue && <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#6E5B51]"><MapPin size={14} /> {dayVenue.name}{dayVenue.postcode ? ` · ${dayVenue.postcode}` : ""}</span>}
                  </div>
                  {dayVenue && <VenueMap venue={dayVenue} className="mt-3 max-w-xl" />}
                  <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {list.map((c) => (
                      <Link key={c.id} to={`/classes/${c.slug}`} data-testid={`class-card-${c.slug}`} className="card-hover flex flex-col rounded-2xl border border-border bg-card p-6">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="font-display text-lg font-bold text-[#2D1E18]">{c.name}</h3>
                          <LevelBadge level={c.level} />
                        </div>
                        {c.temporary_notice && <p className="mt-2 rounded-lg bg-[#FEF3C7] px-2 py-1 text-xs font-semibold text-[#92600A]">{c.temporary_notice}</p>}
                        <div className="mt-4 space-y-2 text-sm text-[#5C4A42]">
                          <p className="inline-flex items-center gap-2"><Clock size={15} className="text-[#6E5B51]" /> {formatTime(c.start_time)} – {formatTime(c.finish_time)}</p>
                          <p className="font-bold text-[#B04B26]">{formatPrice(c.price)} per class</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Container>
    </div>
  );
}
